import getConfig from 'next/config';
import { AltaPayErrorCode } from '~/services/altapay/constants';
import { isAltaPayError, RequestFailure } from './errors';

const { publicRuntimeConfig } = getConfig() || {};

const CHECKOUT_API_URL = `${publicRuntimeConfig.NEXT_PUBLIC_ALTAPAY_API_URL}/checkout/v1/api`;

export type AltaPayRequestInit<PayloadType> = Omit<RequestInit, 'body'> & { body?: PayloadType };
export type DefaultPayload = Record<string, unknown>;

const isJSONResponse = (response: Response) =>
    !!response.headers.get('Content-Type')?.includes('application/json');

const readJSON = (response: Response): Promise<unknown> =>
    isJSONResponse(response) ? response.json().catch(() => undefined) : Promise.resolve(undefined);

/** Converts requests errors to RequestFailure type for uniformity. */
const toRequestFailure = async (response: Response, path: string): Promise<RequestFailure> => {
    const { status } = response;
    const body = await readJSON(response);

    if (isAltaPayError(body)) {
        return {
            errorCode: body.errorCode,
            errorMessage: body.errorMessage ?? 'Request failed.',
            status,
            path,
        };
    }

    // AltaPay does not always send error code 313007 with a 401.
    if (status === 401) {
        return {
            errorCode: AltaPayErrorCode.UNAUTHORIZED,
            errorMessage: 'Token expired. Please update the request before retrying again.',
            status,
            path,
        };
    }

    return { errorMessage: response.statusText || 'Request failed.', status, path };
};

export const request = async <
    ResponseType = unknown,
    PayloadType extends DefaultPayload = DefaultPayload,
>(
    path: string,
    options?: AltaPayRequestInit<PayloadType>,
): Promise<ResponseType> => {
    const url = new URL(CHECKOUT_API_URL + path);

    try {
        const { headers, body, ...rest } = options || {};

        const response = await fetch(url.toString(), {
            method: 'GET',
            ...rest,
            body: body && JSON.stringify(body),
            headers: {
                'Content-Type': 'application/json',
                ...headers,
            },
        });

        if (!response.ok) throw await toRequestFailure(response, path);

        const data = isJSONResponse(response) ? await response.json() : response;

        // AltaPay can send an error body with a 2xx status.
        if (isAltaPayError(data)) throw { ...data, status: response.status, path };

        return data as ResponseType;
    } catch (error) {
        if (isAltaPayError(error)) {
            console.error(`AltaPay - Error ${error.errorCode}: ${error.errorMessage}.`);
        } else {
            console.error(`AltaPay - Error (Internal)\nQuerying: ${path}\n${error}.`);
        }

        throw error;
    }
};
